/* free-gift-gate.js
   Blocks direct add-to-cart of the free-gift products.

   The three free products are $0 rewards that unlock in the cart once the
   shopper passes a spend threshold. Liquid gates (snippets/free-gift-gate.liquid)
   already remove every buy control from the storefront and redirect the product
   page, so there is no button left to press. This closes the path that has no
   button: a hand-rolled fetch from devtools, or a stray script.

   How it distinguishes a legitimate claim from a theft: the cart drawer's CLAIM
   button always stamps the line property `_free_gift` before posting
   (section-cart-drawer.js). Nothing else in the theme does. So an add carrying
   that property is the real unlock flow and passes through; an add without it
   is a direct grab and is rejected.

   NOT a substitute for server-side enforcement. Two paths stay open because
   theme code cannot reach them at all:
     - /cart/<variant_id>:1 permalinks, a Shopify-native route that never runs
       theme code;
     - a POST to /cart/add.js from outside the page (curl, another tab).
   Closing those needs a Shopify Cart & Checkout Validation Function.

   Loaded from layout/theme.liquid as the FIRST deferred script in the document.
   Deferred scripts run in document order, so this wraps window.fetch before
   theme.js or any component script exists — and every /cart/add.js call in the
   theme is made by a click handler those later scripts register. If this tag is
   ever moved below them, the guard stops being reliable.
*/
(function () {
  'use strict';

  var GIFT_PROP = '_free_gift'; /* must match section-cart-drawer.js */

  var variantIds = {};
  var handles = {};

  try {
    var node = document.getElementById('ksc-gift-gate-data');
    var data = node ? JSON.parse(node.textContent) : {};
    (data.variantIds || []).forEach(function (id) { variantIds[String(id)] = true; });
    (data.handles || []).forEach(function (h) { handles[String(h).toLowerCase()] = true; });
  } catch (e) {
    /* Payload missing or malformed: fail open rather than break every add to
       cart in the store. The Liquid gates still hold. */
  }

  function isGatedVariant(id) { return variantIds[String(id)] === true; }
  function isGatedHandle(h) { return handles[String(h).toLowerCase()] === true; }

  window.KSCGiftGate = {
    isGatedVariant: isGatedVariant,
    isGatedHandle: isGatedHandle
  };

  /* ── Is this one add-to-cart item a direct grab? ───────────────────────── */
  function itemIsBlocked(item) {
    if (!item || !isGatedVariant(item.id)) return false;
    var props = item.properties;
    /* Claim flow stamps _free_gift with the gift handle. Anything else — no
       properties at all, or properties without the key — is a direct add. */
    if (props && typeof props === 'object' && props[GIFT_PROP]) return false;
    return true;
  }

  /* Accepts both shapes the theme posts: {items:[…]} and a bare {id, quantity}. */
  function bodyIsBlocked(body) {
    if (typeof body !== 'string') return false; /* FormData etc. — fail open */
    var parsed;
    try { parsed = JSON.parse(body); } catch (e) { return false; }
    if (!parsed) return false;
    var items = parsed.items;
    if (!items) return itemIsBlocked(parsed);
    if (!Array.isArray(items)) return false;
    for (var i = 0; i < items.length; i++) {
      if (itemIsBlocked(items[i])) return true;
    }
    return false;
  }

  /* Shaped like Shopify's own cart error so the theme's existing handlers —
     `r.ok ? r.json() : r.json().then(reject)` — report it without changes. */
  function rejection() {
    var payload = {
      status: 422,
      message: 'Cannot add free gift',
      description: 'Free gifts unlock in your cart once you reach the spend threshold.'
    };
    return new Response(JSON.stringify(payload), {
      status: 422,
      headers: { 'Content-Type': 'application/json' }
    });
  }

  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function' && typeof window.Response === 'function') {
    window.fetch = function (input, init) {
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        if (url.indexOf('/cart/add') !== -1 && init && bodyIsBlocked(init.body)) {
          return Promise.resolve(rejection());
        }
      } catch (e) {
        /* Never let a guard failure swallow a legitimate add. */
      }
      return nativeFetch.apply(this, arguments);
    };
  }

  /* ── Cart notice after a gated product page redirects here ─────────────── */
  /* Liquid has no access to the query string, so the flag is read here and put
     on <html>. A class there survives the cart drawer re-rendering its own
     section markup, which a class on the notice itself would not. */
  try {
    if (window.location.search.indexOf('gift_locked=1') !== -1) {
      document.documentElement.classList.add('ksc-gift-locked');
    }
  } catch (e) {}
}());
